import { Signal } from '@preact/signals-react'
import { BasePlugin } from 'Core/PluginCore/BasePlugin'
import { PluginConnector } from 'Core/PluginCore/PluginConnector'
import { PluginConfigType } from 'Core/PluginCore/PluginsManager'
import { Studio } from 'Core/Studio'
import { BlockStateFormatKey, ITaskBlock } from 'shared/express-interface/blocks-core/ITaskBlock'
// @ts-ignore
import h2p from 'html2plaintext'
import { convertMarkdownToHTML } from 'Utils/markdown.util'
import { ViolationSource } from 'shared/violations/types'

export type ReadabilityPluginConfigType = {
    /**
     *
     */
}

export type ReadabilityType =
    | 'Professor'
    | 'College Student'
    | 'Twelfth Grade'
    | 'Eleventh Grade'
    | 'Tenth Grade'
    | 'Ninth Grade'
    | 'Eighth Grade'
    | 'Seventh Grade'
    | 'Sixth Grade'
    | 'Fifth Grade'
    | 'Fourth Grade'
    | 'Third Grade'
    | 'Second Grade'
    | 'First Grade'
    | 'Kindergarten'

export const ReadabilityColorRangeMap: Record<
    ReadabilityType,
    { min: number; max: number; color: `#${string}` }
> = {
    Professor: { min: 14, max: Infinity, color: '#C90A0A' },
    'College Student': { min: 13, max: 14, color: '#C90A0A' },
    'Twelfth Grade': { min: 12, max: 13, color: '#C92F0B' },
    'Eleventh Grade': { min: 11, max: 12, color: '#C92F0B' },
    'Tenth Grade': { min: 10, max: 11, color: '#C95D0F' },
    'Ninth Grade': { min: 9, max: 10, color: '#C95D0F' },
    'Eighth Grade': { min: 8, max: 9, color: '#B68C26' },
    'Seventh Grade': { min: 7, max: 8, color: '#B68C26' },
    'Sixth Grade': { min: 6, max: 7, color: '#7F8B26' },
    'Fifth Grade': { min: 5, max: 6, color: '#7F8B26' },
    'Fourth Grade': { min: 4, max: 5, color: '#7F8B26' },
    'Third Grade': { min: 3, max: 4, color: '#7F8B26' },
    'Second Grade': { min: 2, max: 3, color: '#7F8B26' },
    'First Grade': { min: 1, max: 2, color: '#4C7D2A' },
    Kindergarten: { min: -Infinity, max: 1, color: '#4C7D2A' },
}

export class ReadabilityPlugin extends BasePlugin<PluginConfigType<ReadabilityPluginConfigType>> {
    public readonly id = 'Readability'
    constructor(
        config: PluginConfigType<ReadabilityPluginConfigType>,
        pluginConnectors: PluginConnector[]
    ) {
        super(config, pluginConnectors)
    }

    public async initialize(studio: Studio) {
        this.studio = studio

        this.pluginConnectors.forEach(connector => {
            const data = connector.getData()
            const connectedBlocks = connector.getConnectedBlocks()
            if (data['text']) {
                data['text'].subscribe(() => {
                    this.handleEventOnChange(connector.id, data, connectedBlocks)
                })
            }
        })
    }

    private async handleEventOnChange(
        connectorId: string,
        data: Record<string, Signal<unknown> | null>,
        connectedBlocks: ITaskBlock[]
    ) {
        if (!data['text']) {
            return
        }
        const value = data['text'].value
        const text = h2p(
            data['text'].type === BlockStateFormatKey.Markdown
                ? convertMarkdownToHTML(value as string)
                : value
        )
        const readability = this.computeReadability(text)
        const readabilityDetails = this.getReadabilityDetails(readability)
        this.handleViolation(connectorId, readability)
        this.handleAnalysisItem(readabilityDetails, connectedBlocks)
    }

    private handleAnalysisItem(
        readabilityDetails: { color: `#${string}`; label: string } | undefined,
        connectedBlocks: ITaskBlock[]
    ) {
        if (readabilityDetails) {
            connectedBlocks.forEach(block => {
                block.setTags?.('readability', {
                    label: 'Readability',
                    value: readabilityDetails.label,
                    colorHex: readabilityDetails.color,
                })
            })
        } else {
            connectedBlocks.forEach(block => {
                block.setTags?.('readability', undefined)
            })
        }
    }

    private handleViolation(connectorId: string, readability: number | undefined) {
        if (
            readability === undefined ||
            readability <= ReadabilityColorRangeMap['Eighth Grade'].max
        ) {
            this.studio?.inputManager.removeViolation?.(`${connectorId}_readability`)
            return
        }

        this.studio?.inputManager.addViolation?.({
            type: 'warning',
            id: `${connectorId}_readability`,
            message:
                readability >= ReadabilityColorRangeMap['Twelfth Grade'].min
                    ? 'Warning: Ensure readability is under Grade 9, except for technical material.'
                    : 'Warning: Ensure readability is under Grade 9, unless specified by the client or for technical content.',
            source: ViolationSource.Plugin,
        })
    }

    private getReadabilityDetails(readability: number | undefined) {
        if (readability === undefined) {
            return
        }
        const readabilityKvp = Object.entries(ReadabilityColorRangeMap).find(rc => {
            const [, attrs] = rc
            return attrs.max > readability && attrs.min <= readability
        })
        if (!readabilityKvp) {
            return undefined
        }
        return { color: readabilityKvp[1].color, label: readabilityKvp[0] }
    }

    private computeReadability(text: string): number | undefined {
        if (!text) {
            return
        }

        const characterCount = text.trim().match(/\w/g)?.length ?? 0
        const wordCount = text.trim().match(/\S+/g)?.length ?? 0
        const sentenceCount =
            text
                .trim()
                .match(/[^\r\n.!?]+(:?(:?\r\n|[\r\n]|[.!?])+|$)/gi)
                ?.filter(item => item.trim().replaceAll('\n', '')).length ?? 0

        if (characterCount === 0 || wordCount === 0 || sentenceCount === 0) {
            return
        }

        const ari = 4.71 * (characterCount / wordCount) + 0.5 * (wordCount / sentenceCount) - 21.43
        return ari > 0 ? ari : 0
    }
}
